import { EventSource } from "@/common/buses";
import { useCallback, useEffect, useState } from "react";
import { apiMain } from "@/common/services";
import { showNotification } from "@/components/alerts/Notifications";
import {
  setAddTagFeedback,
  setRemoveTagFeedback,
  loadAddTagFeedback,
  loadRemoveTagFeedback
} from "@/common/utils/storage";

interface IUseTagFeedback {
  source: EventSource;
  gifId: string;
}

interface ITagFeedbackUpdate {
  tag: string;
  enabled: boolean;
  feedbackMessage: string;
  hasNotification: boolean;
  feedbackCallback: () => void;
}

export const useTagFeedback = ({ gifId, source }: IUseTagFeedback) => {
  const [tagFeedbackMode, setTagFeedbackMode] = useState<boolean>(false);
  const [irrelevantTags, setIrrelevantTags] = useState<string[]>([]);
  const [tagFeedbackLoading, setTagFeedbackLoading] = useState<boolean>(false);

  const getIrrelevantTags = useCallback(async () => {
    setTagFeedbackLoading(true);
    try {
      const response = await apiMain.getGifTagsFeedback(gifId);
      setIrrelevantTags(response.tags);
    } catch (err) {
      console.error(`Could not get irrelevant gif tags: ${err}`);
    } finally {
      setTagFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tagFeedbackMode && !irrelevantTags.length) {
      getIrrelevantTags();
    }
  }, [tagFeedbackMode, getIrrelevantTags]);

  const handleTagFeedbackUpdate = useCallback(async ({
    tag,
    enabled,
    feedbackMessage,
    hasNotification,
    feedbackCallback
  }: ITagFeedbackUpdate) => {
    try {
      await apiMain.updateGifTagFeedback(gifId, {
        page: source,
        tags: [{ tag_id: tag, enabled }],
      });

      feedbackCallback();

      if (hasNotification) {
        showNotification({
          variant: enabled ? "info" : "success",
          title: enabled ? "Feedback Removed" : "Success!",
          description: feedbackMessage,
          actionButton: {
            title: "Got It",
            onClick: () => null,
          },
          secondaryButton: {
            title: "Don't Show Again",
            onClick: enabled ? () => setRemoveTagFeedback(true) : () => setAddTagFeedback(true),
          },
        });
      }
    } catch (err) {
      console.error(`Could not update gif tags feedback: ${err}`);
      showNotification({
        variant: "error",
        title: "Could not add tag feedback, please try again later.",
      });
    }
  }, [gifId, source]);

  const onSendTagFeedback = useCallback(async (tag: string) => {
    const addTagFeedback = loadAddTagFeedback();
    const removeTagFeedback = loadRemoveTagFeedback();

    const isTagIrrelevant = irrelevantTags.includes(tag);
    const feedbackMessage = isTagIrrelevant
      ? `Your feedback for ${tag} has been removed. Thanks for your support.`
      : `Feedback for ${tag} has been received. Thank you for supporting our community.`;

    await handleTagFeedbackUpdate({
      tag,
      enabled: isTagIrrelevant,
      hasNotification: isTagIrrelevant ? !removeTagFeedback : !addTagFeedback,
      feedbackCallback: () => {
        setIrrelevantTags(prev =>
          isTagIrrelevant ? prev.filter(item => item !== tag) : [...new Set([...prev, tag])]
        );
      },
      feedbackMessage
    });
  }, [handleTagFeedbackUpdate, irrelevantTags]);

  const onTagFeedbackMode = useCallback((enabled: boolean) => {
    setTagFeedbackMode(enabled);
  }, []);

  return {
    onSendTagFeedback,
    irrelevantTags,
    tagFeedbackLoading,
    tagFeedbackMode,
    onTagFeedbackMode,
  };
};
